import { isDesktopNotificationsEnabled, setDesktopNotificationsEnabled } from 'services/notifications/osNotification';
import { connectSocket, connected, disconnectSocket } from 'services/messaging/socketIo';
import { persistConfigToStorage, loadSettings } from 'services/settings/settingsStorage';
import { tournamentEngine, fixtures, factoryConstants } from 'tods-competition-factory';
import { removeProviderTournament } from 'services/storage/removeProviderTournament';
import { preferencesConfig, type PreferencesConfig } from 'config/preferencesConfig';
import { getLoginState } from 'services/authentication/loginState';
import { renderForm, validators } from 'courthive-components';
import { removeTournament } from 'services/apis/servicesApi';
import { tmxToast } from 'services/notifications/tmxToast';
import { setActiveScale } from 'settings/setActiveScale';
import { scheduleConfig } from 'config/scheduleConfig';
import { featureFlags } from 'config/featureFlags';
import { tmx2db } from 'services/storage/tmx2db';
import { serverConfig } from 'config/serverConfig';
import { deviceConfig } from 'config/deviceConfig';
import { context } from 'services/context';
import { platform } from 'platform';
import { i18next, t } from 'i18n';
import {
  applyFont,
  applyFontSize,
  FONT_OPTIONS,
  FONT_SIZE_OPTIONS,
  getFontPreference,
  getFontSizePreference,
} from 'services/theme/themeService';

// constants
import { SUPER_ADMIN, TMX_TOURNAMENTS } from 'constants/tmxConstants';

/** Move text spans into their preceding label.radio so radio+text wrap as a unit. */
function fixRadioWrapping(formEl: HTMLElement): void {
  const labels = formEl.querySelectorAll('label.radio');
  for (const label of labels) {
    const span = label.nextElementSibling;
    if (span?.tagName === 'SPAN') {
      label.appendChild(span);
    }
  }
}

const { ratingsParameters } = fixtures;
const { SINGLES } = factoryConstants.eventConstants;

const RATINGS_PANEL_BLUE = 'settings-panel panel-blue';

function persistAll(
  languageInputs: any,
  ratingInputs: any,
  scoringInputs: any,
  schedulingInputs: any,
  storageInputs: any,
  displayInputs: any,
): void {
  const activeScale = ratingInputs?.activeRating?.value;
  serverConfig.set({ saveLocal: storageInputs.saveLocal.checked });
  featureFlags.set({
    googleSheetsImport: displayInputs.googleSheetsImport?.checked || false,
    schedule2: displayInputs.schedule2?.checked || false,
    enableChat: displayInputs.enableChat?.checked || false,
  });

  // Immediately update nav icon visibility
  const s2Icon = document.getElementById('s2-route');
  if (s2Icon) s2Icon.style.display = featureFlags.get().schedule2 ? '' : 'none';
  const chatEl = document.getElementById('chatIndicator');
  if (chatEl) chatEl.style.display = featureFlags.get().enableChat ? '' : 'none';

  let scoringApproach: PreferencesConfig['scoringApproach'];
  if (scoringInputs.dynamicSets.checked) {
    scoringApproach = 'dynamicSets';
  } else if (scoringInputs.dialPad.checked) {
    scoringApproach = 'dialPad';
  } else if (scoringInputs.freeScore.checked) {
    scoringApproach = 'freeScore';
  } else if (scoringInputs.inlineScoring?.checked) {
    scoringApproach = 'inlineScoring';
  } else {
    scoringApproach = 'dynamicSets';
  }
  preferencesConfig.set({
    scoringApproach,
    smartComplements: scoringInputs.smartComplements?.checked || false,
  });

  const minCourtGridRowsValue = schedulingInputs.minCourtGridRows.value;
  if (validators.numericRange(1, 100)(minCourtGridRowsValue)) {
    scheduleConfig.set({ minCourtGridRows: Number.parseInt(minCourtGridRowsValue, 10) });
  }

  const language = languageInputs.language.value;
  const languageChanged = language !== i18next.language;

  if (activeScale) setActiveScale(activeScale);

  persistConfigToStorage({ language });

  if (languageChanged) {
    globalThis.location.reload();
  }
}

export function renderSettingsGrid(container: HTMLElement, options?: { excludeTournament?: boolean }): void {
  const currentSettings = loadSettings();

  // These are populated after renderForm calls; persist closure captures the refs.
  let languageInputs: any;
  let ratingInputs: any;
  let scoringInputs: any;
  let schedulingInputs: any;
  let storageInputs: any;
  let displayInputs: any;

  const persist = () =>
    persistAll(languageInputs, ratingInputs, scoringInputs, schedulingInputs, storageInputs, displayInputs);

  const grid = document.createElement('div');
  grid.className = 'settings-grid';

  // --- Language panel (blue, 1 col) ---
  const languagePanel = document.createElement('div');
  languagePanel.className = RATINGS_PANEL_BLUE;
  languagePanel.innerHTML = `<h3><i class="fa-solid fa-globe"></i> ${t('modals.settings.language')}</h3>`;

  const languageForm = document.createElement('div');
  languageInputs = renderForm(languageForm, [
    {
      options: [
        { value: 'en', label: 'English', selected: i18next.language === 'en' },
        { value: 'fr', label: 'Fran\u00e7ais', selected: i18next.language === 'fr' },
        { value: 'es', label: 'Espa\u00f1ol', selected: i18next.language === 'es' },
        { value: 'pt-BR', label: 'Portugu\u00eas (Brasil)', selected: i18next.language === 'pt-BR' },
        { value: 'de', label: 'Deutsch', selected: i18next.language === 'de' },
        { value: 'ar', label: '\u0627\u0644\u0639\u0631\u0628\u064a\u0629', selected: i18next.language === 'ar' },
        { value: 'zh-CN', label: '\u7b80\u4f53\u4e2d\u6587', selected: i18next.language === 'zh-CN' },
      ],
      onChange: persist,
      field: 'language',
      id: 'language',
    },
  ]);
  languagePanel.appendChild(languageForm);
  grid.appendChild(languagePanel);

  // --- Scoring panel (green, cols 3-4) ---
  const scoringPanel = document.createElement('div');
  scoringPanel.className = 'settings-panel panel-green';
  scoringPanel.style.gridColumn = '2 / 5';
  scoringPanel.innerHTML = `<h3><i class="fa-solid fa-table-tennis-paddle-ball"></i> ${t('modals.settings.scoringApproach')}</h3>`;

  const scoringForm = document.createElement('div');
  scoringInputs = renderForm(scoringForm, [
    {
      options: [
        {
          text: t('modals.settings.dynamicSets'),
          field: 'dynamicSets',
          checked: preferencesConfig.get().scoringApproach === 'dynamicSets',
        },
        {
          text: t('modals.settings.dialPad'),
          field: 'dialPad',
          checked: preferencesConfig.get().scoringApproach === 'dialPad',
        },
        {
          text: t('modals.settings.freeScore'),
          field: 'freeScore',
          checked: preferencesConfig.get().scoringApproach === 'freeScore',
        },
        {
          text: t('modals.settings.inlineScoring'),
          field: 'inlineScoring',
          checked: preferencesConfig.get().scoringApproach === 'inlineScoring',
        },
      ],
      onChange: persist,
      field: 'scoringApproach',
      id: 'scoringApproach',
      radio: true,
    },
    {
      label: t('modals.settings.smartComplements'),
      checked: currentSettings?.smartComplements || false,
      field: 'smartComplements',
      id: 'smartComplements',
      onChange: persist,
      checkbox: true,
    },
  ]);
  // workaround: courthive-components <=0.9.27 doesn't wire onChange for radios
  scoringInputs.dynamicSets.addEventListener('change', persist);
  scoringInputs.dialPad.addEventListener('change', persist);
  scoringInputs.freeScore.addEventListener('change', persist);
  fixRadioWrapping(scoringForm);
  scoringPanel.appendChild(scoringForm);
  grid.appendChild(scoringPanel);

  // --- Scheduling panel (orange, cols 1-2) ---
  const schedulingPanel = document.createElement('div');
  schedulingPanel.className = 'settings-panel panel-orange';
  schedulingPanel.innerHTML = `<h3><i class="fa-solid fa-calendar-days"></i> ${t('modals.settings.scheduling')}</h3>`;

  const schedulingForm = document.createElement('div');
  schedulingInputs = renderForm(schedulingForm, [
    {
      label: t('modals.settings.minScheduleGridRows'),
      value: currentSettings?.minCourtGridRows ?? scheduleConfig.get().minCourtGridRows,
      field: 'minCourtGridRows',
      id: 'minCourtGridRows',
      validator: validators.numericRange(1, 100),
      error: t('modals.settings.minScheduleGridRowsError'),
      selectOnFocus: true,
      onInput: () => {
        const value = schedulingInputs.minCourtGridRows.value;
        if (validators.numericRange(1, 100)(value)) persist();
      },
    },
  ]);
  schedulingPanel.appendChild(schedulingForm);
  grid.appendChild(schedulingPanel);

  // --- Font panel (blue, 1 col — next to scheduling) ---
  const fontPanel = document.createElement('div');
  fontPanel.className = RATINGS_PANEL_BLUE;
  fontPanel.innerHTML = `<h3><i class="fa-solid fa-font"></i> ${t('modals.settings.font')}</h3>`;

  const currentFont = getFontPreference();
  const fontForm = document.createElement('div');
  renderForm(fontForm, [
    {
      options: Object.entries(FONT_OPTIONS).map(([key, { label }]) => ({
        value: key,
        label,
        selected: key === currentFont,
      })),
      onChange: (e: Event) => {
        const select = e.target as HTMLSelectElement;
        applyFont(select.value);
      },
      field: 'fontFamily',
      id: 'fontFamily',
    },
  ]);
  fontPanel.appendChild(fontForm);

  const currentFontSize = getFontSizePreference();
  const fontSizeForm = document.createElement('div');
  fontSizeForm.style.marginTop = '8px';
  renderForm(fontSizeForm, [
    {
      options: Object.entries(FONT_SIZE_OPTIONS).map(([key, { label }]) => ({
        value: key,
        label,
        selected: key === currentFontSize,
      })),
      onChange: (e: Event) => {
        const select = e.target as HTMLSelectElement;
        applyFontSize(select.value);
      },
      field: 'fontSize',
      id: 'fontSize',
    },
  ]);
  fontPanel.appendChild(fontSizeForm);

  grid.appendChild(fontPanel);

  // --- Storage panel (purple, cols 3-4) ---
  const storagePanel = document.createElement('div');
  storagePanel.className = 'settings-panel panel-purple';
  storagePanel.style.gridColumn = '3 / 5';
  storagePanel.innerHTML = `<h3><i class="fa-solid fa-floppy-disk"></i> ${t('modals.settings.storage')}</h3>`;

  const storageForm = document.createElement('div');
  storageInputs = renderForm(storageForm, [
    {
      label: t('modals.settings.saveLocalCopies'),
      checked: serverConfig.get().saveLocal,
      field: 'saveLocal',
      id: 'saveLocal',
      onChange: persist,
      checkbox: true,
    },
  ]);
  storagePanel.appendChild(storageForm);
  grid.appendChild(storagePanel);

  // --- Beta Features panel (teal, 1 col) ---
  const displayPanel = document.createElement('div');
  displayPanel.className = 'settings-panel panel-teal';
  displayPanel.innerHTML = `<h3><i class="fa-solid fa-display"></i> ${t('modals.settings.betaFeatures')}</h3>`;

  const displayForm = document.createElement('div');
  displayInputs = renderForm(displayForm, [
    {
      label: t('modals.settings.googleSheetsImport'),
      checked: featureFlags.get().googleSheetsImport || false,
      field: 'googleSheetsImport',
      id: 'googleSheetsImport',
      onChange: persist,
      checkbox: true,
    },
    {
      label: t('modals.settings.schedule2'),
      checked: featureFlags.get().schedule2 || false,
      field: 'schedule2',
      id: 'schedule2',
      onChange: persist,
      checkbox: true,
    },
    {
      label: 'Tournament Chat',
      checked: featureFlags.get().enableChat || false,
      field: 'enableChat',
      id: 'enableChat',
      onChange: persist,
      checkbox: true,
    },
  ]);
  if (deviceConfig.get().isElectron) {
    const notifRow = document.createElement('div');
    notifRow.style.cssText = 'display:flex;align-items:center;margin-top:8px';
    const notifCheckbox = document.createElement('input');
    notifCheckbox.type = 'checkbox';
    notifCheckbox.id = 'desktopNotifications';
    notifCheckbox.checked = isDesktopNotificationsEnabled();
    notifCheckbox.addEventListener('change', () => setDesktopNotificationsEnabled(notifCheckbox.checked));
    const notifLabel = document.createElement('label');
    notifLabel.htmlFor = 'desktopNotifications';
    notifLabel.textContent = ' Desktop Notifications';
    notifLabel.style.marginLeft = '4px';
    notifRow.appendChild(notifCheckbox);
    notifRow.appendChild(notifLabel);
    displayForm.appendChild(notifRow);
  }
  displayPanel.appendChild(displayForm);
  grid.appendChild(displayPanel);

  // --- Connection panel (indigo, cols 3-4) — Electron only ---
  if (deviceConfig.get().isElectron) {
    const connectionPanel = document.createElement('div');
    connectionPanel.className = 'settings-panel panel-indigo';
    connectionPanel.style.gridColumn = '3 / 5';
    connectionPanel.innerHTML = `<h3><i class="fa-solid fa-server"></i> Connection</h3>`;

    const serverUrlLabel = document.createElement('label');
    serverUrlLabel.className = 'label';
    serverUrlLabel.textContent = 'Server URL';
    serverUrlLabel.style.marginBottom = '4px';

    const serverUrlInput = document.createElement('input');
    serverUrlInput.className = 'input';
    serverUrlInput.type = 'text';
    serverUrlInput.value = serverConfig.get().socketPath || '';
    serverUrlInput.placeholder = 'http://localhost:8383';

    const statusDot = document.createElement('span');
    const updateStatus = () => {
      const isConnected = connected();
      statusDot.style.cssText = `display:inline-block;width:10px;height:10px;border-radius:50%;margin-right:6px;background:${isConnected ? '#48c774' : '#f14668'}`;
    };
    updateStatus();

    const statusLabel = document.createElement('span');
    statusLabel.style.fontSize = '0.85rem';
    const updateStatusLabel = () => {
      statusLabel.textContent = connected() ? 'Connected' : 'Disconnected';
    };
    updateStatusLabel();

    const statusRow = document.createElement('div');
    statusRow.style.cssText = 'display:flex;align-items:center;margin-top:8px;margin-bottom:8px';
    statusRow.appendChild(statusDot);
    statusRow.appendChild(statusLabel);

    const offlineCheckbox = document.createElement('input');
    offlineCheckbox.type = 'checkbox';
    offlineCheckbox.id = 'workOffline';
    offlineCheckbox.checked = !serverConfig.get().serverFirst;

    const offlineLabel = document.createElement('label');
    offlineLabel.htmlFor = 'workOffline';
    offlineLabel.textContent = ' Work Offline';
    offlineLabel.style.marginLeft = '4px';

    const offlineRow = document.createElement('div');
    offlineRow.style.cssText = 'display:flex;align-items:center;margin-top:8px';
    offlineRow.appendChild(offlineCheckbox);
    offlineRow.appendChild(offlineLabel);

    // Save server URL on blur
    serverUrlInput.addEventListener('change', () => {
      const url = serverUrlInput.value.trim();
      serverConfig.set({ socketPath: url });
      platform.setServerUrl?.(url);
      persistConfigToStorage({});
    });

    // Work Offline toggle
    offlineCheckbox.addEventListener('change', () => {
      if (offlineCheckbox.checked) {
        disconnectSocket();
        serverConfig.set({ serverFirst: false, saveLocal: true });
      } else {
        serverConfig.set({ serverFirst: true });
        connectSocket(() => {
          updateStatus();
          updateStatusLabel();
        });
      }
      updateStatus();
      updateStatusLabel();
      persistConfigToStorage({});
    });

    connectionPanel.appendChild(serverUrlLabel);
    connectionPanel.appendChild(serverUrlInput);
    connectionPanel.appendChild(statusRow);
    connectionPanel.appendChild(offlineRow);
    grid.appendChild(connectionPanel);
  }

  // --- Active Rating panel (blue, 1 col) — tournament-specific ---
  if (!options?.excludeTournament) {
    const ratingPanel = document.createElement('div');
    ratingPanel.className = RATINGS_PANEL_BLUE;
    ratingPanel.innerHTML = `<h3><i class="fa-solid fa-star"></i> ${t('modals.settings.activeRating')}</h3>`;

    // Discover which ratings are present in current tournament participants
    const { participants: allParticipants = [] } = tournamentEngine.getParticipants({ withScaleValues: true }) ?? {};
    const presentRatings = new Set<string>();
    for (const p of allParticipants) {
      for (const item of p.ratings?.[SINGLES] || []) {
        presentRatings.add(item.scaleName);
      }
    }

    // Build rating options: tournament ratings first, then all others (excluding deprecated)
    const allRatingKeys = Object.keys(ratingsParameters).filter((key) => !(ratingsParameters as any)[key].deprecated);
    const inTournament = allRatingKeys.filter((key) => presentRatings.has(key));
    const notInTournament = allRatingKeys.filter((key) => !presentRatings.has(key));

    const ratingOptions = [
      ...inTournament.map((key) => ({
        label: `${key} (in tournament)`,
        value: key.toLowerCase(),
        selected: preferencesConfig.get().activeScale === key.toLowerCase(),
      })),
      ...notInTournament.map((key) => ({
        label: key,
        value: key.toLowerCase(),
        selected: preferencesConfig.get().activeScale === key.toLowerCase(),
      })),
    ];

    const ratingForm = document.createElement('div');
    ratingInputs = renderForm(ratingForm, [
      {
        options: ratingOptions,
        onChange: persist,
        field: 'activeRating',
        id: 'activeRating',
      },
    ]);
    ratingPanel.appendChild(ratingForm);
    grid.appendChild(ratingPanel);
  }

  // --- Delete Tournament panel (red, full width) — tournament-specific ---
  if (!options?.excludeTournament) {
    const tournamentRecord = tournamentEngine.getTournament().tournamentRecord;
    const provider = tournamentRecord?.parentOrganisation;
    const providerId = provider?.organisationId;
    const state = getLoginState();
    const superAdmin = state?.roles?.includes(SUPER_ADMIN);
    const canDeleteOnServer = superAdmin || state?.permissions?.includes('deleteTournament');
    const activeProvider = context.provider || state?.provider;

    const isProviderTournament = !!(activeProvider && providerId);
    if (tournamentRecord && (!isProviderTournament || canDeleteOnServer)) {
      const deletePanel = document.createElement('div');
      deletePanel.className = 'settings-panel panel-red';
      deletePanel.style.gridColumn = '3 / 5';
      deletePanel.innerHTML = `<h3><i class="fa-solid fa-trash"></i> ${t('modals.settings.dangerZone')}</h3>`;

      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'button is-danger is-outlined';
      deleteBtn.style.marginTop = '8px';
      deleteBtn.innerHTML = `<i class="fa-solid fa-trash" style="margin-right:6px"></i>${t('modals.tournamentActions.deleteTournament')}`;
      deleteBtn.addEventListener('click', () => {
        const tournamentId = tournamentRecord.tournamentId;
        const provId = state?.providerId || providerId;
        const navigateAway = async () => {
          tournamentEngine.reset();
          if (provId) await removeProviderTournament({ tournamentId, providerId: provId });
          context.router?.navigate(`/${TMX_TOURNAMENTS}`);
        };
        const localDelete = () => tmx2db.deleteTournament(tournamentId).then(navigateAway);

        tmxToast({
          action: {
            onClick: () => {
              if (activeProvider && provId && canDeleteOnServer) {
                removeTournament({ providerId: provId, tournamentId }).then(localDelete, (err) => {
                  console.error('[deleteTournament] server error:', err);
                  localDelete();
                });
              } else {
                localDelete();
              }
            },
            text: t('common.confirm'),
          },
          message: t('modals.tournamentActions.deleteTournament'),
          intent: 'is-danger',
        });
      });

      deletePanel.appendChild(deleteBtn);
      grid.appendChild(deletePanel);
    }
  }

  container.appendChild(grid);
}
